import React, { useLayoutEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../../../client';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import ScrollShadow from '../../../../components/ScrollShadow';
import { __ } from '../../../../i18n/l10n';
import { Card } from '../../../../styled-components/dark/common';
import GRViewWrapper from '../GRViewWrapper/GRViewWrapper';

const sharedMarginStyle = css`
  margin-bottom: 1.3em;
`;

const StyledGRViewWrapper = styled(GRViewWrapper)`
  padding: 2.5em;
  text-align: center;
  color: var(--GREnrollment-defaultColor);
`;

const GRLogo = styled.img`
  content: var(--GREnrollment-logoImgPathUrl);
  max-width: 100%;
  max-height: 20%;
  margin-bottom: 1em;
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2em;
  max-height: 80%;

  background-color: #11111177;

  text-align: center;
  border-radius: ${Card.borderRadius};
`;

const Rules = styled(ScrollShadow)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  margin-bottom: 1em;
`;

const Rule = styled.div`
  ${sharedMarginStyle}
`;

const RuleTitle = styled.div`
  font-weight: bold;
  margin-bottom: 0.3em;
  color: var(--GREnrollment-titleColor);
  font-size: 2.2em;
`;

const RuleDescription = styled.div`
  line-height: 1.4em;
  font-family: BettingArchivoNarrow;
  margin-bottom: 1em;
`;

const FullRulesLink = styled.div`
  cursor: pointer;
  color: white;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

type StartButtonProps = {
  $disabled: boolean;
};

const StartButton = styled.div<StartButtonProps>`
  position: relative;

  padding: 2em 4.5em !important;

  transition: none 150ms ease-in-out;
  transition-property: transform, filter;

  background-image: var(--GREnrollment-startButtonBackroundImgPathUrl);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center center;
  user-select: none;

  ${({ $disabled }) => css`
    ${$disabled
      ? css`
          filter: grayscale(1);
          pointer-events: none;
        `
      : css`
          &:active {
            transform: scale(1.05);
          }

          ${!Target.__MOBILE__
            ? css`
                cursor: pointer;

                &:hover {
                  filter: brightness(110%);
                }
              `
            : undefined}
        `}
  `}
`;

const List = styled.ul`
  list-style: none;
  color: var(--GREnrollment-listColor);
  font-family: BettingArchivoNarrow;
  font-weight: bold;
`;

const ListItem = styled.li`
  display: flex;
  gap: 0.3em;
  text-align: left;

  &:not(:last-child) {
    margin-bottom: 0.25em;
  }

  &:before {
    content: '';
    display: flex;
    min-height: 1.5em;
    min-width: 1.5em;
    transform: translateY(0.25em) rotateY(180deg);
    background-image: var(--GREnrollment-listItemIconImgPathUrl);
    background-repeat: no-repeat;
    background-size: contain;
  }
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  z-index: -1;

  background-image: var(--GREnrollment-backgroundImgPathUrl);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
`;

type LoadingSpinnerPropsType = {
  $isLoading: boolean;
};
const StyledLoadingSpinner = styled(LoadingSpinner)<LoadingSpinnerPropsType>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  --LoadingSpinner-color: white;
  transition: opacity 150ms ease-in-out;

  ${({ $isLoading }) => css`
    ${$isLoading
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
        `}
  `}
`;

type StartLabelPropsType = {
  $isLoading: boolean;
};
const StartLabel = styled.div<StartLabelPropsType>`
  transition: opacity 150ms ease-in-out;

  ${({ $isLoading }) => css`
    ${$isLoading
      ? css`
          opacity: 0;
        `
      : css`
          opacity: 1;
        `}
  `}
`;

type GREnrollmentProps = {
  className?: string;
  onFullRulesClick: () => void;
  onEnroll: () => void;
  isEnrollmentDisabled?: boolean;
  mainDescription?: string;
  roundDescriptions: Array<string>;
  isLoading: boolean;
};

const GREnrollment = ({
  className,
  onFullRulesClick,
  onEnroll,
  isEnrollmentDisabled = true,
  mainDescription,
  roundDescriptions,
  isLoading,
}: GREnrollmentProps) => {
  const startLabelNodeRef = useRef<HTMLDivElement>(null);
  const loadingSpinnerWrapperNodeRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const startLabelNode = startLabelNodeRef.current;
    const loadingSpinnerWrapperNode = loadingSpinnerWrapperNodeRef.current;

    if (startLabelNode && loadingSpinnerWrapperNode) {
      const { width: startLabelNodeWidth } = startLabelNode.getBoundingClientRect();

      loadingSpinnerWrapperNode.style.width = `${startLabelNodeWidth}px`;
    }
  }, []);

  return (
    <StyledGRViewWrapper className={className}>
      <Background />
      <GRLogo />
      <CardWrapper>
        <Rules>
          <Rule>
            {mainDescription ? (
              <>
                <RuleTitle>{__('Game objective')}</RuleTitle>
                <RuleDescription>{mainDescription}</RuleDescription>
              </>
            ) : null}
            {roundDescriptions.length ? (
              <List>
                {roundDescriptions.map((roundDescription) => (
                  <ListItem key={roundDescription}>{roundDescription}</ListItem>
                ))}
              </List>
            ) : null}
          </Rule>
          <Rule>
            <RuleTitle>{__('Prizepool')}</RuleTitle>
            <RuleDescription>{__('Win up to %1$s times your reference bet in Freebets!', 100)}</RuleDescription>
          </Rule>

          <FullRulesLink onClick={onFullRulesClick}>{__('Check the full rules')}</FullRulesLink>
        </Rules>
        <StartButton $disabled={isEnrollmentDisabled} onClick={onEnroll}>
          <StartLabel ref={startLabelNodeRef} $isLoading={isLoading}>
            {__('!action:GiftRace Start')}
          </StartLabel>
          <StyledLoadingSpinner ref={loadingSpinnerWrapperNodeRef} $isLoading={isLoading} />
        </StartButton>
      </CardWrapper>
    </StyledGRViewWrapper>
  );
};

export default GREnrollment;
